import * as React from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { useParams, useHistory } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import DisplayDateTime from '../../components/DisplayDateTime';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import DisplayDate from '../../components/DisplayDate';

// ICONS
import { ReactComponent as ArrowLeftIcon } from '../../assets/svg/chevron-left.svg';
import { ReactComponent as EnvelopeIcon } from '../../assets/svg/envelope-icon.svg';
import { ReactComponent as SwitchUserIcon } from '../../assets/svg/switch-user.svg';

// TYPES
import { IncidentEditUser, DomainIncidentType } from '../../api/models';
import { IncidentFormTypes } from './IncidentSubmit';
import { RouteParams } from '../../Interfaces';

interface StepFourTypes {
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;

	leadershipTeamData: IncidentEditUser[];
	otherRecipientsData: IncidentEditUser[];

	isLoading: boolean;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const StepFour: React.FC<StepFourTypes> = ({
	incidentFormData,
	setIncidentFormData,
	setActiveStep,

	leadershipTeamData,
	otherRecipientsData,

	isLoading,
	setIsLoading
}) => {
	const history = useHistory();

	// Get ID from params
	const params = useParams<RouteParams>();
	const incidentId = params.id;

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get all incident Types from Redux store
	const incidentTypes = useAppSelector((state) => state.lists.incidentTypes);

	// Error state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const {
		// STEP ONE
		name,
		age,
		dateOfBirth,
		title,
		occurrence,
		type,
		specification,
		location,
		description,
		witness,
		witnesses,
		identifier,

		// STEP TWO
		firstAidAdministered,
		firstAid,
		medicationAdministered,
		medication,
		medicalPersonnelContacted,
		medicalPersonnel,
		emergencyServiceContacted,
		emergencyServices,
		takenToHospital,
		hospital,
		dateAndTimeTakenToHospital,
		personTakenToHospital,
		relationshipToChild,
		otherActions,
		otherActionsTaken,

		// STEP THREE
		individualsNotified,
		individuals,
		authorityNotified,
		authorities,
		notes,
		newAttachments,
		createAttachments,
		attachments,
		deleteAttachmentIds,
		leaderRecipientIds,
		otherRecipientIds,
		otherRecipientEmails,
		otherRecipients,
		leaders
	} = incidentFormData;

	const createIncidentReport = async (values: any) => {
		if (incidentFormData !== undefined) {
			setIsLoading(true);

			const formData = {
				...incidentFormData,

				reviewer: values.reviewer,
				reviewerRole: values.reviewerRole,

				// Reformat some fields data because if we don't do this, when we summitted a form and provide a Yes field with extra info, once we're in edit mode and changed the field value to No, the extra info will not be removed and will still be submmited when finish editing and resubmit the form again.
				witnesses: incidentFormData.witness ? incidentFormData.witnesses : [],
				firstAid: incidentFormData.firstAidAdministered ? incidentFormData.firstAid : '',
				medication: incidentFormData.medicationAdministered ? incidentFormData.medication : [],
				medicalPersonnel: incidentFormData.medicalPersonnelContacted ? incidentFormData.medicalPersonnel : [],
				emergencyServices: incidentFormData.emergencyServiceContacted ? incidentFormData.emergencyServices : [],
				hospital: incidentFormData.takenToHospital ? incidentFormData.hospital : '',
				dateAndTimeTakenToHospital: incidentFormData.takenToHospital ? incidentFormData.dateAndTimeTakenToHospital : '',
				personTakenToHospital: incidentFormData.takenToHospital ? incidentFormData.personTakenToHospital : '',
				relationshipToChild: incidentFormData.takenToHospital ? incidentFormData.relationshipToChild : '',
				otherActionsTaken: incidentFormData.otherActions ? incidentFormData.otherActionsTaken : '',
				individuals: incidentFormData.individualsNotified ? incidentFormData.individuals : [],
				authorities: incidentFormData.authorityNotified ? incidentFormData.authorities : []
			};

			setIncidentFormData(formData);

			const response = await apibridge.submitIncidentReport(formData);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				history.push('/incident-reports');

				useUpdateTotalNotification();
			}
		}
	};

	// FORM SET UP
	const initialValuesStepFour = {
		reviewer: incidentFormData.reviewer || '',
		reviewerRole: incidentFormData.reviewerRole || '',
		reviewerConfirmed: undefined,
		informationConfirmed: undefined,
		signatureConfirmed: undefined
	};

	const validationSchemaStepFour = Yup.object({
		reviewer: Yup.string().required('Required'),
		reviewerRole: Yup.string().required('Required'),
		reviewerConfirmed: Yup.bool().oneOf([true], 'Required').required('Required'),
		informationConfirmed: Yup.bool().oneOf([true], 'Required').required('Required'),
		signatureConfirmed: Yup.bool().oneOf([true], 'Required').required('Required')
	});

	// Logic to work out current User Detail
	const activeProfileId = useAppSelector((state) => state.user.activeProfileId);
	const profiles = useAppSelector((state) => state.user.profiles);
	const activeProfile = profiles?.find((item) => item.id === activeProfileId);

	const firstName = useAppSelector((state) => state.user.firstName);
	const lastName = useAppSelector((state) => state.user.lastName);
	const currentRole = activeProfile?.roleName;

	// Display Type and Specification
	const allTypes = useAppSelector((state) => state.lists.incidentTypes);
	const allSpecifications = useAppSelector((state) => state.lists.incidentSpecifications) || [];

	const currentType = allTypes?.find((item) => item.value === type);

	const currentAllSpecification = allSpecifications.find((item) => item.value === currentType?.value) || {
		items: [{ value: '', label: '' }],
		value: ''
	};

	const currentSpecification = currentAllSpecification?.items?.find((item: any) => item.value === specification);

	return (
		<div>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<Formik
								initialValues={initialValuesStepFour}
								validationSchema={validationSchemaStepFour}
								onSubmit={createIncidentReport}
								enableReinitialize
							>
								{(formik) => {
									return (
										<Form translate="yes" className="general-form">
											<h2 className="general-header">Please review this incident report before submitting</h2>

											<p className="sub-grey-text mbtom-24">
												The nominated contacts will be notified to acknowledge the incident.
											</p>

											<div className="general-notification-box">
												<SwitchUserIcon />

												<div className="info-text w-100">
													<p>
														Please have a supervisor or manager review this incident report before submitting it for
														acknowledgement.
													</p>
												</div>
											</div>

											<div className="general-form-layout-wrapper">
												<div className="complaints-detail w-100">
													{/* INCIDENT DETAILS */}

													<h3>Incident Details</h3>

													<div className="general-divider"></div>

													<div className="detail-block row">
														<div className="col-4">
															<h4 className="detail-heading">Child's name</h4>

															<p className="detail-info">{name}</p>
														</div>

														<div className="col-5">
															<h4 className="detail-heading">Child's age</h4>

															<p className="detail-info">{age}</p>
														</div>
													</div>

													{dateOfBirth ? (
														<div className="detail-block w-60">
															<h2 className="detail-heading">Child’s date of birth</h2>

															<p className="detail-info">
																<DisplayDate>{dateOfBirth}</DisplayDate>
															</p>
														</div>
													) : null}

													<div className="detail-block w-60">
														<h2 className="detail-heading">Incident title</h2>

														<p className="detail-info">{title}</p>
													</div>

													{identifier ? (
														<div className="detail-block w-60">
															<h2 className="detail-heading">Incident ID</h2>

															<p className="detail-info">{identifier}</p>
														</div>
													) : null}

													<div className="detail-block w-60">
														<h4 className="detail-heading">Date and time of incident</h4>

														<time className="detail-info">
															<DisplayDateTime>{occurrence}</DisplayDateTime>
														</time>
													</div>

													<div className="detail-block w-60">
														<h4 className="detail-heading">Incident type</h4>

														<p className="detail-info">
															{currentType?.label} - {currentSpecification?.label}
														</p>
													</div>

													<div className="detail-block w-60">
														<h4 className="detail-heading">Location of the incident</h4>

														<p className="detail-info">{location}</p>
													</div>

													<div className="detail-block w-60">
														<h4 className="detail-heading">Description of the incident</h4>

														<p className="detail-info">{description}</p>
													</div>

													<div className="detail-block">
														<h4 className="detail-heading">Incident witnesses</h4>

														{witness ? (
															<>
																{witnesses &&
																	witnesses.length > 0 &&
																	witnesses.map((item, index) => {
																		return (
																			<div key={index} className="row incident-review-detail-row">
																				<div className="col-4">
																					<p className="name">{item.name}</p>

																					<p>{item.role}</p>
																				</div>

																				<div className="col-5">
																					<p>{item.email}</p>

																					<p>{item.phone}</p>
																				</div>
																			</div>
																		);
																	})}
															</>
														) : (
															<p className="detail-info">None</p>
														)}
													</div>

													{/* ACTIONS TAKEN */}
													<h3>Actions taken</h3>

													<div className="general-divider"></div>

													<div className="detail-block w-60">
														<h4 className="detail-heading">First aid administered</h4>

														<p className="detail-info">{firstAidAdministered ? <>{firstAid}</> : 'None'}</p>
													</div>

													<div className="detail-block">
														<h4 className="detail-heading">Medication administered</h4>

														{medicationAdministered ? (
															<>
																{medication &&
																	medication.length > 0 &&
																	medication.map((item, index) => {
																		return (
																			<div key={index} className="row incident-review-detail-row">
																				<div className="col-12">
																					<p className="name">{item.name}</p>
																				</div>

																				<div className="col-4">
																					<p>Dosage of {item.dosage}</p>

																					<p>
																						<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																					</p>
																				</div>

																				<div className="col-5">
																					<p>by {item.personAdministered}</p>

																					<p>{item.role}</p>
																				</div>
																			</div>
																		);
																	})}
															</>
														) : (
															<p className="detail-info">None</p>
														)}
													</div>

													<div className="detail-block">
														<h4 className="detail-heading">Medical personnel contacted</h4>

														{medicalPersonnelContacted ? (
															<>
																{medicalPersonnel &&
																	medicalPersonnel.length > 0 &&
																	medicalPersonnel.map((item, index) => {
																		return (
																			<div key={index} className="row incident-review-detail-row">
																				<div className="col-4">
																					<p className="name">{item.name}</p>

																					<p>{item.role}</p>
																				</div>

																				<div className="col-5">
																					<p>
																						Contacted <DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																					</p>

																					<p>{item.phoneOrEmail}</p>
																				</div>
																			</div>
																		);
																	})}
															</>
														) : (
															<p className="detail-info">None</p>
														)}
													</div>

													<div className="detail-block">
														<h4 className="detail-heading">Emergency services contacted</h4>

														{emergencyServiceContacted ? (
															<>
																{emergencyServices &&
																	emergencyServices.length > 0 &&
																	emergencyServices.map((item, index) => {
																		return (
																			<div key={index} className="row incident-review-detail-row">
																				<div className="col-4">
																					<p className="name">{item.name}</p>
																				</div>

																				<div className="col-5">
																					<p>
																						Contacted <DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																					</p>

																					{item.attend ? (
																						<p>
																							Arrived <DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																						</p>
																					) : (
																						<p>Did not attend</p>
																					)}
																				</div>
																			</div>
																		);
																	})}
															</>
														) : (
															<p className="detail-info">None</p>
														)}
													</div>

													<div className="detail-block w-60">
														<h4 className="detail-heading">Transported to hospital</h4>

														{takenToHospital ? (
															<>
																<p className="detail-info">{hospital}</p>

																<p className="detail-info">
																	<DisplayDateTime>{dateAndTimeTakenToHospital}</DisplayDateTime>
																</p>

																<p className="detail-info">
																	by {personTakenToHospital}, {relationshipToChild}
																</p>
															</>
														) : (
															<p className="detail-info">None</p>
														)}
													</div>

													<div className="detail-block w-60">
														<h4 className="detail-heading">Other actions taken</h4>

														<p className="detail-info">{otherActions ? <>{otherActionsTaken}</> : 'None'}</p>
													</div>

													{/* NOTIFICATION */}

													<h3>Notification</h3>

													<div className="general-divider"></div>

													<div className="detail-block">
														<h4 className="detail-heading">Individuals notified</h4>

														{individualsNotified ? (
															<>
																{individuals &&
																	individuals.length > 0 &&
																	individuals.map((item, index) => {
																		return (
																			<div key={index} className="row incident-review-detail-row dark-text">
																				<div className="col-12">
																					<p className="name">{item.name}</p>
																				</div>

																				<div className="col-4">
																					<p>{item.role}</p>

																					<p>
																						<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																					</p>
																				</div>

																				<div className="col-5">
																					<p>{item.contactMethod}</p>

																					<p>Notification {item.isNotifiedSuccess ? 'successful' : 'unsuccessful'}</p>
																				</div>
																			</div>
																		);
																	})}
															</>
														) : (
															<p className="detail-info">None</p>
														)}
													</div>

													<div className="detail-block">
														<h4 className="detail-heading">Authorities notified</h4>

														{authorityNotified ? (
															<>
																{authorities &&
																	authorities.length > 0 &&
																	authorities.map((item, index) => {
																		return (
																			<div key={index} className="row incident-review-detail-row dark-text">
																				<div className="col-4">
																					<p className="name">{item.authorityName}</p>

																					<p>{item.notifiedName}</p>

																					<p>
																						<DisplayDateTime>{item.dateAndTime}</DisplayDateTime>
																					</p>
																				</div>

																				<div className="col-5">
																					<p>{item.reason}</p>

																					<p>{item.contactMethod}</p>

																					<p>Notification {item.isNotifiedSuccess ? 'successful' : 'unsuccessful'}</p>
																				</div>
																			</div>
																		);
																	})}
															</>
														) : (
															<p className="detail-info">None</p>
														)}
													</div>

													{/* RECIPIENTS OF THE REPORT */}
													<h3>Recipients of the report</h3>

													<div className="general-divider mbtom-0"></div>

													<section className="info-section w-50 mtop-0">
														<ul>
															{leadershipTeamData &&
																leadershipTeamData.length > 0 &&
																leadershipTeamData.map((item) => {
																	if (
																		item.id &&
																		incidentFormData.leaderRecipientIds &&
																		incidentFormData.leaderRecipientIds.includes(item.id.toString())
																	) {
																		return (
																			<li key={item.id} className="row-container">
																				<div className="row detail-row no-border">
																					<div className="col-5 col-sm-2 col-lg-1 pdl-0">
																						<CircleImage
																							firstName={item.name}
																							lastName={item.name}
																							src={item.profileImage}
																						/>
																					</div>

																					<div className="col-7 col-sm-10 col-lg-11">
																						<div className="row detail-info">
																							<div className="col-lg-12 detail-name">
																								<p className="detail-name mbtom-0">{item.name}</p>

																								<p className="label grey mbtom-0">{item.role}</p>
																							</div>
																						</div>
																					</div>
																				</div>
																			</li>
																		);
																	} else {
																		return null;
																	}
																})}
														</ul>

														<ul>
															{otherRecipientsData &&
																otherRecipientsData.length > 0 &&
																otherRecipientsData.map((item) => {
																	if (
																		item.id &&
																		incidentFormData.otherRecipientIds &&
																		incidentFormData.otherRecipientIds.includes(item.id.toString())
																	) {
																		return (
																			<li key={item.id} className="row-container">
																				<div className="row detail-row no-border">
																					<div className="col-5 col-sm-2 col-lg-1 pdl-0">
																						<CircleImage
																							firstName={item.name}
																							lastName={item.name}
																							src={item.profileImage}
																						/>
																					</div>

																					<div className="col-7 col-sm-10 col-lg-11">
																						<div className="row detail-info">
																							<div className="col-lg-12 detail-name">
																								<p className="detail-name mbtom-0">{item.name}</p>

																								<p className="label grey mbtom-0">{item.role}</p>
																							</div>
																						</div>
																					</div>
																				</div>
																			</li>
																		);
																	} else {
																		return null;
																	}
																})}
														</ul>

														<ul>
															{otherRecipientEmails &&
																otherRecipientEmails.length > 0 &&
																otherRecipientEmails.map((item, index) => {
																	return (
																		<li key={index} className="row-container">
																			<div className="row detail-row no-border">
																				<div className="col-5 col-sm-2 col-lg-1 pdl-0">
																					<div className="incident-icon-container">
																						<EnvelopeIcon />
																					</div>
																				</div>

																				<div className="col-7 col-sm-10 col-lg-11">
																					<div className="row detail-info">
																						<div className="col-12 detail-name">{item}</div>
																					</div>
																				</div>
																			</div>
																		</li>
																	);
																})}
														</ul>
													</section>

													{/* ADDITIONAL INFORMATION */}
													{notes ||
													(attachments && attachments.length > 0) ||
													(newAttachments && newAttachments.length > 0) ? (
														<>
															<h3 className="mtop-30">Additional information</h3>

															<div className="general-divider"></div>
														</>
													) : null}

													{notes ? (
														<div className="detail-block w-60">
															<h2 className="detail-heading">Notes</h2>

															<p className="detail-info">{notes}</p>
														</div>
													) : null}

													{((attachments && attachments.length > 0) ||
														(newAttachments && newAttachments.length > 0)) && (
														<div className="detail-block w-60">
															<h4 className="detail-heading">Attachments</h4>

															<ul>
																{attachments &&
																	attachments.length > 0 &&
																	attachments
																		.filter((item) => !deleteAttachmentIds?.includes(item.id || ''))
																		.map((item, index) => {
																			return (
																				<li key={index}>
																					<div className="attachments-download-link">
																						<span className="link-name">{item.displayFileName}</span>
																					</div>
																				</li>
																			);
																		})}

																{newAttachments &&
																	newAttachments.length > 0 &&
																	newAttachments.map((item, index) => {
																		return (
																			<li key={index}>
																				<div className="attachments-download-link">
																					<span className="link-name">{item.displayFileName}</span>
																				</div>
																			</li>
																		);
																	})}
															</ul>
														</div>
													)}

													{/* PERSON WHO REVIEWED THIS REPORT */}
													<h3 className="mtop-40">Person who reviewed this report</h3>

													<div className="general-divider"></div>

													<div className="row row-w-60">
														<div className="form-group col-lg-6 mbtom-0">
															<label htmlFor="reviewer" className="label">
																Name of the supervisor/manager
															</label>

															<div className="form-field mbtom-20">
																<Field name="reviewer" id="reviewer" type="text" className="form-input" />

																<ErrorMessage name="reviewer" component="div" className="error-message" />
															</div>
														</div>

														<div className="form-group col-lg-6 mbtom-0 gap-right-0">
															<label htmlFor="reviewerRole" className="label">
																Role
															</label>

															<div className="form-field mbtom-20">
																<Field name="reviewerRole" id="reviewerRole" type="text" className="form-input" />

																<ErrorMessage name="reviewerRole" component="div" className="error-message" />
															</div>
														</div>
													</div>

													<div className="form-group w-60">
														<label className="form-control-checkbox">
															<Field
																name="reviewerConfirmed"
																type="checkbox"
																onChange={() => {
																	formik.setFieldValue('reviewerConfirmed', !formik.values.reviewerConfirmed);
																}}
															/>

															<span>
																I confirm that the reviewer of this report authorises the distribution of this incident
																report to all nominated contacts.
															</span>
														</label>

														<ErrorMessage name="reviewerConfirmed">
															{(error) => <div className="form-validation-error">{error}</div>}
														</ErrorMessage>
													</div>

													{/* PERSON WHO CREATED THIS REPORT */}
													<h3 className="mtop-10">Person who created this report</h3>

													<div className="general-divider"></div>

													<div className="detail-block">
														<div className="row incident-review-detail-row normal-text">
															<div className="col-3">
																<h4 className="detail-heading">Reported by</h4>

																<p>
																	{firstName} {lastName}
																</p>

																<p>(yourself)</p>
															</div>

															<div className="col-5">
																<h4 className="detail-heading">Role</h4>

																<p>{currentRole}</p>
															</div>
														</div>
													</div>

													<div className="incident-electronic-acknowledgement">
														<p className="sub-grey-text">Electronic Signature Acknowledgement</p>

														<p className="signature">
															{firstName} {lastName}
														</p>
													</div>

													<div className="form-group w-60">
														<label className="form-control-checkbox">
															<Field
																name="informationConfirmed"
																type="checkbox"
																onChange={() => {
																	formik.setFieldValue('informationConfirmed', !formik.values.informationConfirmed);
																}}
															/>

															<span>
																I acknowledge that all information in this incident report is true and correct and that
																any attachments are appropriate, do not contain intimate images of children's body
																parts, protect children's privacy and comply with the law.
															</span>
														</label>

														<ErrorMessage name="informationConfirmed">
															{(error) => <div className="form-validation-error">{error}</div>}
														</ErrorMessage>
													</div>

													<div className="form-group w-60">
														<label className="form-control-checkbox">
															<Field
																name="signatureConfirmed"
																type="checkbox"
																onChange={() => {
																	formik.setFieldValue('signatureConfirmed', !formik.values.signatureConfirmed);
																}}
															/>

															<span>
																I understand that I am signing this form using an Electronic Signature and I consent to
																the use of an Electronic Signature as the legal equivalent of a handwritten signature.
															</span>
														</label>

														<ErrorMessage name="signatureConfirmed">
															{(error) => <div className="form-validation-error">{error}</div>}
														</ErrorMessage>
													</div>
												</div>
											</div>

											{/* SUBMIT BUTTON */}
											<div className="general-divider"></div>

											<div className="form-two-button-group">
												<Button
													disabled={isLoading}
													buttonIcon={<ArrowLeftIcon className="link-icon" />}
													className="btn-secondary edit-btn"
													onClick={() => setActiveStep('step1')}
												>
													Edit details
												</Button>

												{isOrganisationActive && (
													<Button disabled={isLoading} className="btn-primary submit-btn" type="submit">
														Submit report
													</Button>
												)}
											</div>
										</Form>
									);
								}}
							</Formik>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default StepFour;
